import React, { useEffect, useRef, useState, useCallback } from "react";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Play, Pause, ArrowCounterClockwise, Camera, ArrowsOut, Info } from "@phosphor-icons/react";
import { flattenEntities, playEventsToKanban } from "../../lib/gamecraft";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Builds a self-contained Phaser 3 HTML document injected into the iframe.
// Receives the full project object so it can embed sprites as base64 and
// inject the user's code files directly.
// ---------------------------------------------------------------------------
function buildPhaserHTML(project, testConfig = {}) {
  const name = project.name || "Game";
  const controls = project.controls?.keyboard || {};
  const files = (project.files || []).filter((f) => {
    const p = (f.path || "").toLowerCase();
    const lang = (f.language || "").toLowerCase();
    return lang.includes("javascript") || lang.includes("typescript") || /\.(js|mjs|ts)$/.test(p);
  });
  const sprites = project.sprites || [];
  const ents = (project.entities && Object.values(project.entities).flat()) || [];
  const test = {
    floors: true, creatures: true, gear: true, mechanics: true, buffs: true, drops: true,
    crt: false, hitboxes: false, slowMo: false, shake: true, particles: true,
    enemyCount: 3, dropRate: 35, weaponDamage: 12, speedMul: 1, dmgTakenMul: 1,
    floorTex: "", creatureTex: "", weaponTex: "",
    ...testConfig,
  };

  const camera = project.config?.camera === "topdown" ? "topdown" : "side";
  const gravityY = camera === "topdown" ? 0 : 600;
  const gameName = (project.name || "Game").replace(/[<>]/g, "");

  const spriteLoaders = sprites
    .map((s, i) => {
      const key = s.name ? s.name.replace(/\s+/g, "_") : `sprite_${i}`;
      const url = s.data_url || "";
      if (!url.startsWith("data:")) return "";
      return `    this.load.image('${key}', '${url}');`;
    })
    .filter(Boolean)
    .join("\n") || "    // no sprites";

  const playerTexture = sprites.find((s) => s.subtype === "hero" || s.name === "Hero")
    || sprites.find((s) => (s.category || "") === "character")
    || sprites.find((s) => (s.studio || s.category) === "creatures" || (s.category || "").match(/character|player/))
    || sprites[0];
  const playerKey = playerTexture?.name ? playerTexture.name.replace(/\s+/g, "_") : null;
  const baseName = (playerTexture?.name || "Hero").replace(/_walk.*$/i, "").replace(/\s+/g, "_");
  const walkKeys = sprites
    .filter((s) => {
      const n = (s.name || "").replace(/\s+/g, "_");
      return /walk/i.test(n) && (n.startsWith(baseName) || s.subtype === (playerTexture?.subtype || "hero"));
    })
    .map((s) => s.name.replace(/\s+/g, "_"));
  const idleWalk = playerKey ? [playerKey, ...walkKeys.filter((k) => k !== playerKey)] : walkKeys;
  const mobWalkKeys = sprites
    .filter((s) => (s.subtype === "mob" || s.category === "mob" || /slime/i.test(s.name || "")) && /walk|slime/i.test(s.name || ""))
    .map((s) => s.name.replace(/\s+/g, "_"));
  const allEnts = flattenEntities(project);
  const heroEnt = allEnts.find((e) => e.kind === "characters" || e.subtype === "hero") || {};
  const mobEnt = allEnts.find((e) => ["mobs", "enemies", "minibosses", "bosses"].includes(e.kind)) || {};
  const sfxPads = (project.soundboard || []).map((p) => ({
    id: p.id, label: p.label, f: p.f, type: p.type, t: p.t, tag: p.tag || p.id,
  }));
  const objective = project.gdd?.objective || "Recoge la espada y limpia la sala";

  const up    = controls.move_up    || "W";
  const down  = controls.move_down  || "S";
  const left  = controls.move_left  || "A";
  const right = controls.move_right || "D";
  const jump  = controls.jump       || "Space";
  const attackK = (!controls.attack || String(controls.attack).startsWith("Mouse")) ? "J" : controls.attack;
  const interact = controls.interact || "E";
  const throwK = controls.throw || "F";
  const ability = (!controls.ability || String(controls.ability).startsWith("Mouse")) ? "Shift" : controls.ability;
  const useK = controls.secondary && !String(controls.secondary).startsWith("Mouse") ? controls.secondary : "Q";

  const userCode = files.length > 0
    ? files.map(f => `\ntry {\n// ── ${f.path} ──\n${f.content || ""}\n} catch (err) { console.warn('user code', err); }`).join("\n")
    : "// No JS files yet. Python se prueba al exportar, no dentro de Phaser.";

  const enemyCount = Math.min(Number(test.enemyCount) || ents.filter(e => ["enemies","mobs","bosses","minibosses"].includes(e.kind)).length || 3, 10);
  const hud = {
    ...(project.config?.hud || {}),
    assets: {
      heart: (project.sprites || []).find((s) => s.name === "HUD_heart")?.data_url || "",
      portrait: (project.sprites || []).find((s) => s.name === "HUD_retrato")?.data_url || "",
      weapon: (project.sprites || []).find((s) => s.category === "weapon" || s.name === "Espada")?.data_url || "",
      potion: (project.sprites || []).find((s) => /pocion|potion/i.test(s.name || "") || s.category === "item")?.data_url || "",
    },
  };
  const hudJson = JSON.stringify(hud).replace(/</g, "\\u003c");
  const testJson = JSON.stringify({ ...test, enemyCount }).replace(/</g, "\\u003c");
  const sfxJson = JSON.stringify(sfxPads).replace(/</g, "\\u003c");
  const walkJson = JSON.stringify(idleWalk).replace(/</g, "\\u003c");
  const mobWalkJson = JSON.stringify(mobWalkKeys).replace(/</g, "\\u003c");

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${name}</title>
  <script src="https://cdn.jsdelivr.net/npm/phaser@3.80.1/dist/phaser.min.js"></script>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { background:#09090b; overflow:hidden; }
    #gf-hud {
      position:fixed; top:8px; left:8px; right:8px;
      display:flex; gap:8px; font:11px/1 'JetBrains Mono',monospace;
      color:#5cf2ff; pointer-events:none; z-index:100;
    }
    #gf-hud-custom { position:fixed; inset:0; pointer-events:none; z-index:95; }
    .hud-pill {
      background:rgba(9,9,11,0.82); border:1px solid #1e3a4a;
      padding:4px 8px; border-radius:8px; backdrop-filter:blur(8px);
    }
    .hud-heart { font-size:16px; letter-spacing:2px; }
    .hud-bar { background:#1e293b; border:1px solid #334155; overflow:hidden; }
    .hud-bar > i { display:block; height:100%; }
    .hud-score { font:700 13px 'JetBrains Mono',monospace; color:#facc15; }
    .hud-slot { width:28px; height:28px; border:2px solid #334155; background:#0f172a; display:inline-flex; align-items:center; justify-content:center; margin-right:4px; transition:transform .08s; }
    .hud-slot.on { transform:scale(1.18); border-color:#22d3ee; }
    #gf-buffs { position:fixed; top:8px; right:8px; display:flex; gap:4px; z-index:96; pointer-events:none; }
    .hud-buff { font:9px monospace; padding:3px 6px; border:1px solid #334155; background:#0f172a; color:#facc15; text-transform:uppercase; }
    .hud-portrait { width:44px; height:44px; border:2px solid #e8f0ff; background:#0f172a; image-rendering:pixelated; }
    .hud-weapon { width:40px; height:40px; border:2px solid #e0b040; background:#0f172a; image-rendering:pixelated; }
    #gf-crt {
      display:none; position:fixed; inset:0; pointer-events:none; z-index:90;
      background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,.18) 3px);
      mix-blend-mode: multiply;
    }
    #gf-gameover {
      display:none; position:fixed; inset:0;
      background:rgba(9,9,11,0.88);
      align-items:center; justify-content:center; flex-direction:column; gap:16px;
      font-family:'JetBrains Mono',monospace; color:#ef4444; font-size:24px;
      font-weight:700; letter-spacing:0.2em;
    }
    #gf-gameover button {
      font-family:inherit; font-size:12px; letter-spacing:0.3em;
      background:#22d3ee; color:#09090b; border:none; padding:8px 24px; cursor:pointer;
    }
  </style>
</head>
<body>
<div id="gf-hud">
  <div class="hud-pill">FPS: <span id="hud-fps">0</span></div>
  <div class="hud-pill" id="hud-debug-hp" style="display:none">HP: <span id="hud-hp">100</span></div>
  <div class="hud-pill" style="color:#a1a1aa">
    ${camera === "topdown" ? `${up}/${left}/${down}/${right}` : `${up}/${left}/${down}/${right}+${jump}`} · ${interact} agarrar · ${throwK} tirar · ${attackK} atacar · ${ability} roll · ${useK} usar
  </div>
</div>
<div id="gf-hud-custom"></div>
<div id="gf-crt"></div>
<div id="gf-gameover">
  // GAME OVER
  <button onclick="window.__restartGame && window.__restartGame()">RESTART (R / ESPACIO)</button>
</div>

<script>
// ── injected user code ──────────────────────────────────────────
${userCode}
// ────────────────────────────────────────────────────────────────

const GF_CONFIG = {
  playerTexture: ${playerKey ? `'${playerKey}'` : "null"},
  camera: '${camera}',
  controls: { up:'${up}', down:'${down}', left:'${left}', right:'${right}', jump:'${jump}', attack:'${attackK}', interact:'${interact}', throw:'${throwK}', ability:'${ability}', use:'${useK}' },
  enemyCount: ${enemyCount},
  playerHp: ${Number(heroEnt.hp) || 100},
  playerDmg: ${Number(heroEnt.damage) || 12},
  enemyHp: ${Number(mobEnt.hp) || 28},
  enemyDmg: ${Number(mobEnt.damage) || 6},
  objective: ${JSON.stringify(objective)},
  walkKeys: ${walkJson},
  mobWalkKeys: ${mobWalkJson},
};
function gfLog(msg) { try { parent.postMessage({ type: 'gf-log', msg: String(msg) }, '*'); } catch (e) {} }
function playLog(evt) { try { parent.postMessage({ type: 'gf-playlog', evt: String(evt), t: Date.now() }, '*'); } catch (e) {} }
const GF_TEST = ${testJson};
const GF_HUD = ${hudJson};
const GF_SFX = ${sfxJson};
if (GF_TEST.crt) document.getElementById('gf-crt').style.display = 'block';

function sfx(tag) {
  try {
    const pad = (GF_SFX || []).find(p => p.tag === tag || p.id === tag || String(p.label||'').toLowerCase() === tag) || { f: 440, type: 'square', t: 0.1 };
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    window._ac = window._ac || new AC();
    const ctx = window._ac;
    if (ctx.state === 'suspended') ctx.resume();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    const f = Number(pad.f) || 440;
    const t = Number(pad.t) || 0.12;
    o.type = pad.type || 'square';
    o.frequency.setValueAtTime(f, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(Math.max(40, f * 0.4), ctx.currentTime + t);
    g.gain.setValueAtTime(0.16, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + t + 0.02);
  } catch (e) {}
}

let _hp = 100, _score = 0, _combo = 0, _inv = [], _sel = 0, _hudBuilt = false;
function paintHud() {
  const root = document.getElementById('gf-hud-custom');
  if (!root) return;
  const widgets = GF_HUD.widgets || [];
  if (!widgets.length) {
    document.getElementById('hud-debug-hp').style.display = 'block';
    return;
  }
  const heartsN = Math.max(0, Math.round((_hp / (GF_CONFIG.playerHp || 100)) * (widgets.find(w => w.type==='hearts')?.count || 5)));
  const pct = Math.max(0, Math.min(100, (_hp / (GF_CONFIG.playerHp || 100)) * 100));
  if (!_hudBuilt) {
    root.innerHTML = widgets.map((w, idx) => {
      const st = 'position:absolute;left:'+w.x+'px;top:'+w.y+'px;';
      if (w.type === 'hearts') {
        const n = w.count || 5;
        let html = '';
        for (let i=0;i<n;i++) {
          if (GF_HUD.assets && GF_HUD.assets.heart) html += '<img data-h="'+i+'" src="'+GF_HUD.assets.heart+'" style="width:20px;height:20px;image-rendering:pixelated;opacity:1"/>';
          else html += '<span data-h="'+i+'" style="color:#e23b4a">♥</span>';
        }
        return '<div data-wid="'+idx+'" data-type="hearts" style="'+st+'display:flex;gap:2px">'+html+'</div>';
      }
      if (w.type === 'bar') {
        return '<div data-wid="'+idx+'" data-type="bar" style="'+st+'"><div style="font:9px monospace;color:#94a3b8">'+(w.label||'HP')+'</div><div class="hud-bar" style="width:'+(w.w||140)+'px;height:'+(w.h||10)+'px"><i style="width:'+pct+'%;background:'+(w.color||'#e23b4a')+'"></i></div></div>';
      }
      if (w.type === 'score') return '<div data-wid="'+idx+'" data-type="score" class="hud-score" style="'+st+'">SCORE '+String(_score).padStart(4,'0')+'</div>';
      if (w.type === 'portrait') {
        const img = (GF_HUD.assets && GF_HUD.assets.portrait) ? '<img class="hud-portrait" src="'+GF_HUD.assets.portrait+'"/>' : '<div class="hud-portrait"></div>';
        return '<div data-wid="'+idx+'" style="'+st+'">'+img+'</div>';
      }
      if (w.type === 'slots') {
        const n = w.count || 5;
        let s = '';
        for (let i = 0; i < n; i++) s += '<span class="hud-slot" data-slot="'+i+'"></span>';
        return '<div data-wid="'+idx+'" data-type="slots" style="'+st+'">'+s+'</div>';
      }
      if (w.type === 'weapon') {
        const img = (GF_HUD.assets && GF_HUD.assets.weapon) ? '<img class="hud-weapon" src="'+GF_HUD.assets.weapon+'"/>' : '<div class="hud-weapon"></div>';
        return '<div data-wid="'+idx+'" style="'+st+'">'+img+'</div>';
      }
      if (w.type === 'boss') return '<div data-wid="'+idx+'" style="'+st+'"><div style="font:9px monospace;color:#f87171">BOSS</div><div class="hud-bar" style="width:240px;height:8px"><i style="width:55%;background:#ef4444"></i></div></div>';
      return '';
    }).join('') + '<div id="gf-buffs"></div>';
    _hudBuilt = true;
  }
  widgets.forEach((w, idx) => {
    const el = root.querySelector('[data-wid="'+idx+'"]');
    if (!el) return;
    if (w.type === 'hearts') {
      el.querySelectorAll('[data-h]').forEach((node) => {
        const i = Number(node.getAttribute('data-h'));
        if (node.tagName === 'IMG') node.style.opacity = i < heartsN ? 1 : 0.25;
        else node.style.color = i < heartsN ? '#e23b4a' : '#334155';
      });
    }
    if (w.type === 'bar') {
      const i = el.querySelector('i');
      if (i) i.style.width = pct + '%';
    }
    if (w.type === 'score') el.textContent = 'SCORE ' + String(_score).padStart(4,'0');
    if (w.type === 'slots') {
      el.querySelectorAll('[data-slot]').forEach((slot) => {
        const i = Number(slot.getAttribute('data-slot'));
        const it = _inv[i];
        slot.className = 'hud-slot' + (i === _sel ? ' on' : '');
        let inner = '';
        if (it && GF_HUD.assets) {
          const src = GF_HUD.assets[it.kind] || GF_HUD.assets.weapon;
          if (src) inner = '<img src="'+src+'" style="width:22px;height:22px;image-rendering:pixelated"/>';
        }
        if (slot.innerHTML !== inner) slot.innerHTML = inner;
      });
    }
  });
  const b = document.getElementById('gf-buffs');
  if (b) {
    const chips = []
      .concat((GF_TEST.buffs || []).map((x) => '<span class="hud-buff">'+x+'</span>'))
      .concat((GF_TEST.debuffs || []).map((x) => '<span class="hud-buff" style="color:#fb7185">'+x+'</span>'));
    b.innerHTML = chips.join('');
  }
}
function setHp(v) { _hp = Math.max(0, v); const el = document.getElementById('hud-hp'); if (el) el.textContent = Math.round(_hp); paintHud(); }
function addScore(v) { _score += v; paintHud(); }
function setCombo(v) { _combo = v; }
function setDrop(t) { }
paintHud();

class BootScene extends Phaser.Scene {
  constructor() { super('Boot'); }
  preload() {
    // Create a fallback 32×32 cyan texture programmatically
    const g = this.make.graphics({ x:0, y:0, add:false });
    g.fillStyle(0x22d3ee); g.fillRect(0,0,32,32);
    g.generateTexture('__player_fallback', 32, 32);
    g.destroy();

    const ge = this.make.graphics({ x:0, y:0, add:false });
    ge.fillStyle(0xef4444); ge.fillRect(0,0,24,24);
    ge.generateTexture('__enemy_fallback', 24, 24);
    ge.destroy();

    // Load project sprites (base64, no network)
    ${spriteLoaders}
  }
  create() { this.scene.start('Menu'); }
}

class MenuScene extends Phaser.Scene {
  constructor() { super('Menu'); }
  create() {
    const W = this.scale.width, H = this.scale.height;
    this.add.rectangle(W/2, H/2, W, H, 0x0b1220);
    this.add.text(W/2, H/2 - 36, ${JSON.stringify(gameName)}, { fontFamily: 'monospace', fontSize: '22px', color: '#5cf2ff' }).setOrigin(0.5);
    this.add.text(W/2, H/2, GF_CONFIG.camera === 'topdown' ? 'CAMARA ARRIBA  ·  WASD' : 'LADO A LADO  ·  SALTO', { fontFamily: 'monospace', fontSize: '11px', color: '#94a3b8' }).setOrigin(0.5);
    this.add.text(W/2, H/2 + 18, GF_CONFIG.objective || 'Recoge la espada y limpia la sala', { fontFamily: 'monospace', fontSize: '12px', color: '#facc15' }).setOrigin(0.5);
    this.add.text(W/2, H/2 + 38, 'E agarrar  F tirar  J atacar  Shift roll  Q usar  1-5 slots', { fontFamily: 'monospace', fontSize: '11px', color: '#e2e8f0' }).setOrigin(0.5);
    this.add.text(W/2, H/2 + 58, 'ESPACIO para jugar', { fontFamily: 'monospace', fontSize: '12px', color: '#94a3b8' }).setOrigin(0.5);
    gfLog('Menu listo · ' + GF_CONFIG.camera);
    this.input.keyboard.once('keydown-SPACE', () => this.scene.start('Main'));
    const jmp = this.input.keyboard.addKey(GF_CONFIG.controls.jump === 'Space' ? Phaser.Input.Keyboard.KeyCodes.SPACE : GF_CONFIG.controls.jump.toUpperCase());
    jmp.once('down', () => this.scene.start('Main'));
  }
}

class MainScene extends Phaser.Scene {
  constructor() { super('Main'); }

  create() {
    const W = this.scale.width, H = this.scale.height;
    _hp = GF_CONFIG.playerHp || 100; _score = 0; _inv = []; _sel = 0; _hudBuilt = false; setCombo(0); setDrop('—');
    setHp(_hp);
    this.physics.world.drawDebug = !!GF_TEST.hitboxes;
    this.time.timeScale = GF_TEST.slowMo ? 0.35 : 1;
    this._speedMul = GF_TEST.speedMul || 1;
    this._dmgMul = GF_TEST.dmgTakenMul || 1;
    this._poison = (GF_TEST.debuffs || []).includes('poison');
    this._shield = (GF_TEST.buffs || []).includes('shield');
    this._haste = (GF_TEST.buffs || []).includes('haste');
    if (this._haste) this._speedMul *= 1.45;

    const bg = this.add.graphics();
    bg.lineStyle(1, 0x1e3a4a, 0.35);
    for (let x = 0; x < W; x += 40) bg.lineBetween(x, 0, x, H);
    for (let y = 0; y < H; y += 40) bg.lineBetween(0, y, W, y);

    this.platforms = this.physics.add.staticGroup();
    this.hazards = this.physics.add.staticGroup();
    const topdown = GF_CONFIG.camera === 'topdown';
    gfLog(topdown ? 'Arena top-down' : 'Plataformas side');
    if (GF_TEST.floors !== false) {
      const floorTex = GF_TEST.floorTex && this.textures.exists(GF_TEST.floorTex) ? GF_TEST.floorTex : null;
      if (topdown) {
        [[20, H/2, 16, H], [W-20, H/2, 16, H], [W/2, 20, W, 16], [W/2, H-20, W, 16]].forEach(([x,y,w,h]) => {
          const p = this.add.rectangle(x, y, w, h, 0x1a2a22);
          this.physics.add.existing(p, true);
          this.platforms.add(p);
        });
        const hz = this.add.rectangle(W/2, H/2, 90, 90, 0x7f1d1d, 0.55);
        this.physics.add.existing(hz, true);
        this.hazards.add(hz);
        if (floorTex) this.add.tileSprite(W/2, H/2, W - 40, H - 40, floorTex).setAlpha(0.35);
        const doorR = this.add.rectangle(W - 10, H/2, 14, 56, 0xb45309);
        this.add.text(W - 28, H/2 - 44, 'PUERTA', { fontFamily: 'monospace', fontSize: '8px', color: '#fbbf24' }).setOrigin(0.5);
        this.physics.add.existing(doorR, true);
        this._dummyDoor = doorR;
        const doorL = this.add.rectangle(10, H/2, 14, 56, 0xb45309);
        this.physics.add.existing(doorL, true);
      } else {
        const ground = this.add.rectangle(W/2, H - 16, W, 32, 0x1a2a22);
        this.physics.add.existing(ground, true);
        this.platforms.add(ground);
        const platData = [[150, H-120, 160, 16], [400, H-180, 140, 16], [600, H-240, 180, 16], [200, H-300, 120, 16]];
        platData.forEach(([x,y,w,h]) => {
          const p = this.add.rectangle(x, y, w, h, 0x2a3f38);
          this.physics.add.existing(p, true);
          this.platforms.add(p);
          if (floorTex) this.add.image(x, y, floorTex).setDisplaySize(w, h).setAlpha(0.85);
        });
        const hz = this.add.rectangle(480, H - 40, 80, 16, 0x7f1d1d);
        this.physics.add.existing(hz, true);
        this.hazards.add(hz);
      }
    }

    const pTex = GF_CONFIG.playerTexture || '__player_fallback';
    const texExists = this.textures.exists(pTex);
    this.player = this.physics.add.sprite(topdown ? W/2 : 100, topdown ? H/2 : H - 60, texExists ? pTex : '__player_fallback');
    this.player.setCollideWorldBounds(true);
    this.player.setBounce(topdown ? 0 : 0.1);
    this.player.setMaxVelocity(380, topdown ? 380 : 900);
    this.player.body.allowGravity = !topdown;
    if (!texExists) this.player.setDisplaySize(32, 32);
    const walkFrames = (GF_CONFIG.walkKeys || []).filter(k => this.textures.exists(k));
    if (walkFrames.length >= 2) {
      this.anims.create({ key: 'hero-walk', frames: walkFrames.map(k => ({ key: k })), frameRate: 8, repeat: -1 });
      this.anims.create({ key: 'hero-idle', frames: [{ key: texExists ? pTex : walkFrames[0] }], frameRate: 2 });
      this.player.play('hero-idle');
    }
    const mobFrames = (GF_CONFIG.mobWalkKeys || []).filter(k => this.textures.exists(k));
    if (mobFrames.length >= 2) {
      this.anims.create({ key: 'slime-bounce', frames: mobFrames.map(k => ({ key: k })), frameRate: 5, repeat: -1 });
    }

    this.enemies = this.physics.add.group();
    if (GF_TEST.creatures !== false) {
      const cTex = (GF_TEST.creatureTex && this.textures.exists(GF_TEST.creatureTex)) ? GF_TEST.creatureTex : '__enemy_fallback';
      for (let i = 0; i < (GF_TEST.enemyCount || 3); i++) {
        const e = this.physics.add.sprite(Phaser.Math.Between(80, W - 80), topdown ? Phaser.Math.Between(80, H - 80) : H - 60, cTex);
        e.setCollideWorldBounds(true);
        e.setBounce(0.4);
        e.body.allowGravity = !topdown;
        e.setVelocity(Phaser.Math.Between(-80, 80) || 60, topdown ? Phaser.Math.Between(-80, 80) : 0);
        e.health = GF_CONFIG.enemyHp || 28;
        e.contactDmg = GF_CONFIG.enemyDmg || 6;
        if (cTex !== '__enemy_fallback') e.setDisplaySize(28, 28);
        if (this.anims.exists('slime-bounce')) e.play('slime-bounce');
        this.enemies.add(e);
      }
    }

    this.physics.add.collider(this.player, this.platforms);
    this.physics.add.collider(this.enemies, this.platforms);
    this.physics.add.overlap(this.player, this.hazards, () => {
      if (this._iframes > 0) return;
      this._iframes = 40;
      sfx('hurt');
      playLog('hazard');
      setHp(_hp - 8 * this._dmgMul);
      gfLog('hazard hit');
      if (_hp <= 0) this._triggerGameOver();
    });
    this.physics.add.overlap(this.player, this.enemies, (_player, enemy) => {
      if (this._iframes > 0) return;
      this._iframes = 60;
      const dmg = (enemy.contactDmg || 10) * this._dmgMul * (this._shield ? 0.5 : 1);
      sfx('hurt');
      playLog('hurt');
      setHp(_hp - dmg);
      setCombo(0);
      if (GF_TEST.shake !== false) this.cameras.main.shake(180, 0.008);
      if (_hp <= 0) this._triggerGameOver();
    });
    if (this._dummyDoor) {
      this.physics.add.overlap(this.player, this._dummyDoor, () => {
        if (!this._doorSaid) { this._doorSaid = true; gfLog('Puerta dummy — aún no hay siguiente sala'); playLog('puerta dummy'); }
      });
    }

    const { up, down, left, right, jump, attack, interact, throw: throwKey, ability, use } = GF_CONFIG.controls;
    const codeOf = (k, fb) => {
      const v = (!k || String(k).startsWith('Mouse')) ? fb : k;
      if (v === 'Space') return Phaser.Input.Keyboard.KeyCodes.SPACE;
      if (v === 'Shift') return Phaser.Input.Keyboard.KeyCodes.SHIFT;
      if (v === 'Tab') return Phaser.Input.Keyboard.KeyCodes.TAB;
      return String(v).toUpperCase();
    };
    this.keys = this.input.keyboard.addKeys({
      up:    codeOf(up, 'W'),
      down:  codeOf(down, 'S'),
      left:  codeOf(left, 'A'),
      right: codeOf(right, 'D'),
      jump:  codeOf(jump, 'Space'),
      attack: codeOf(attack, 'J'),
      interact: codeOf(interact, 'E'),
      throw: codeOf(throwKey, 'F'),
      dash: codeOf(ability, 'Shift'),
      use: codeOf(use, 'Q'),
      inv: Phaser.Input.Keyboard.KeyCodes.TAB,
      one: Phaser.Input.Keyboard.KeyCodes.ONE,
      two: Phaser.Input.Keyboard.KeyCodes.TWO,
      three: Phaser.Input.Keyboard.KeyCodes.THREE,
      four: Phaser.Input.Keyboard.KeyCodes.FOUR,
      five: Phaser.Input.Keyboard.KeyCodes.FIVE,
      altUp: Phaser.Input.Keyboard.KeyCodes.UP,
      altLeft: Phaser.Input.Keyboard.KeyCodes.LEFT,
      altRight: Phaser.Input.Keyboard.KeyCodes.RIGHT,
      altDown: Phaser.Input.Keyboard.KeyCodes.DOWN,
    });
    this._topdown = topdown;
    this.input.on('pointerdown', () => { if (GF_TEST.mechanics !== false) this._doAttack(); });

    this.loot = this.physics.add.group();
    this.thrown = this.physics.add.group();
    this.pickups = this.physics.add.staticGroup();
    const potionKey = this.textures.exists('Pocion') ? 'Pocion' : null;
    const swordKey = (GF_TEST.weaponTex && this.textures.exists(GF_TEST.weaponTex)) ? GF_TEST.weaponTex : (this.textures.exists('Espada') ? 'Espada' : null);
    const spawnItem = (x, y, kind, name, key) => {
      let spr;
      if (key) {
        spr = this.physics.add.sprite(x, y, key);
        spr.setDisplaySize(22, 22);
      } else {
        spr = this.add.circle(x, y, 8, kind === 'weapon' ? 0xe0b040 : 0xe23b4a);
        this.physics.add.existing(spr);
      }
      spr.body.allowGravity = !topdown;
      spr.body.setBounce(0.2);
      spr.itemKind = kind;
      spr.itemName = name;
      spr.itemKey = key;
      this.loot.add(spr);
      this.physics.add.collider(spr, this.platforms);
      if (topdown) this.tweens.add({ targets: spr, y: spr.y - 5, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      return spr;
    };
    if (GF_TEST.gear !== false || GF_TEST.mechanics !== false) {
      const spots = topdown
        ? [[160, 140], [360, 200], [520, 160]]
        : [[180, H - 80], [340, H - 200], [560, H - 80]];
      const table = (GF_TEST.lootTable && GF_TEST.lootTable.length)
        ? GF_TEST.lootTable
        : [{ name: 'Poción', rate: 45, kind: 'potion' }, { name: 'Espada', rate: 30, kind: 'weapon' }];
      const pickRow = () => {
        const usable = table.filter((t) => t.kind !== 'empty' && !/^nada$/i.test(t.name || ''));
        const pool = usable.length ? usable : table;
        const total = pool.reduce((s, x) => s + Number(x.rate || 0), 0) || 1;
        let r = Math.random() * total;
        for (const row of pool) { r -= Number(row.rate || 0); if (r <= 0) return row; }
        return pool[pool.length - 1];
      };
      spots.forEach(([x, y], i) => {
        const row = i === spots.length - 1
          ? (table.find((t) => t.kind === 'weapon' || /espada|sword/i.test(t.name || '')) || { name: 'Espada', kind: 'weapon' })
          : pickRow();
        const kind = row.kind || (/espada|sword|arma|weapon/i.test(row.name || '') ? 'weapon' : 'potion');
        const key = kind === 'weapon' ? swordKey : potionKey;
        spawnItem(x, y, kind, row.name || (kind === 'weapon' ? 'Espada' : 'Poción'), key);
      });
      gfLog('Loot de la tabla en el suelo. Mantén E para agarrar.');
    }
    if (GF_TEST.drops !== false) {
      const n = 6;
      for (let i = 0; i < n; i++) {
        const px = Phaser.Math.Between(50, W - 50);
        const py = topdown ? Phaser.Math.Between(80, H - 80) : Phaser.Math.Between(H - 360, H - 80);
        const c = this.add.circle(px, py, 5, 0xfacc15);
        this.physics.add.existing(c, true);
        this.pickups.add(c);
      }
    }
    this.physics.add.overlap(this.player, this.pickups, (_p, pickup) => {
      pickup.destroy();
      addScore(8);
      setCombo(_combo + 1);
      sfx('pickup');
    });
    this.physics.add.overlap(this.thrown, this.enemies, (proj, enemy) => {
      this._hurtEnemy(enemy, 14);
      this._landItem(proj);
    });
    this.physics.add.collider(this.thrown, this.platforms, (proj) => this._landItem(proj));

    this._iframes = 0;
    this._dead = false;
    this._atkCd = 0;
    this._dashCd = 0;
    this._poisonTick = 0;
    this._coyote = 0;
    this._jumpBuf = 0;
    this._wasJump = false;
    this._holdE = 0;
    this._doorSaid = false;
    this.prompt = this.add.text(0, 0, '', { fontFamily: 'monospace', fontSize: '10px', color: '#5cf2ff' }).setOrigin(0.5).setDepth(20).setVisible(false);
    const heldKey = swordKey && this.textures.exists(swordKey) ? swordKey : null;
    this.held = this.add.image(0, 0, heldKey || pTex).setDisplaySize(16, 16).setVisible(false).setDepth(12);
    window.__restartGame = () => {
      document.getElementById('gf-gameover').style.display = 'none';
      this.scene.restart();
    };
    this.input.keyboard.on('keydown-R', () => { if (this._dead) window.__restartGame(); });
    this.input.keyboard.on('keydown-SPACE', () => { if (this._dead) window.__restartGame(); });
  }

  _hurtEnemy(enemy, dmg) {
    if (!enemy || !enemy.active) return;
    enemy.health = (enemy.health || 28) - (dmg || 6);
    enemy.setTint(0xffffff);
    this.time.delayedCall(80, () => { if (enemy.active) enemy.clearTint(); });
    const kb = this.player.flipX ? -160 : 160;
    enemy.setVelocityX((enemy.body && enemy.body.velocity.x || 0) + kb);
    enemy.setVelocityY((enemy.body && enemy.body.velocity.y || 0) - 80);
    this._hitstop();
    sfx('hit');
    playLog('hit');
    if (enemy.health <= 0) {
      addScore(30);
      setCombo(_combo + 1);
      gfLog('enemigo abajo');
      playLog('kill');
      enemy.destroy();
    }
  }

  _hitstop() {
    this.time.timeScale = 0.12;
    window.setTimeout(() => { this.time.timeScale = GF_TEST.slowMo ? 0.35 : 1; }, 50);
  }

  _nearestLoot(range) {
    let best = null, bestD = range || 42;
    this.loot.getChildren().forEach((o) => {
      if (!o.active) return;
      const d = Phaser.Math.Distance.Between(this.player.x, this.player.y, o.x, o.y);
      if (d < bestD) { bestD = d; best = o; }
    });
    return best;
  }

  _landItem(proj) {
    if (!proj || !proj.active) return;
    this.thrown.remove(proj);
    proj.setVelocity(0, 0);
    this.loot.add(proj);
  }

  _doPickup() {
    const near = this._nearestLoot(46);
    if (!near) { gfLog('nada cerca (E)'); return; }
    if (_inv.length >= (window.GC_INV_MAX || 5)) { gfLog('inventario lleno'); return; }
    _inv.push({ name: near.itemName || 'objeto', kind: near.itemKind || 'item', key: near.itemKey });
    _sel = _inv.length - 1;
    near.destroy();
    paintHud();
    sfx('pickup');
    playLog('pickup');
    gfLog('agarraste ' + _inv[_sel].name);
    if (window.GC_ON_PICKUP) window.GC_ON_PICKUP(_inv[_sel]);
  }

  _doThrow() {
    if (!_inv.length) { gfLog('nada que tirar'); return; }
    const i = Math.min(_sel, _inv.length - 1);
    const it = _inv.splice(i, 1)[0];
    if (_sel >= _inv.length) _sel = Math.max(0, _inv.length - 1);
    paintHud();
    const dir = this.player.flipX ? -1 : 1;
    const key = it.key && this.textures.exists(it.key) ? it.key : null;
    const spr = key
      ? this.physics.add.sprite(this.player.x + dir * 20, this.player.y - 6, key)
      : this.add.circle(this.player.x + dir * 20, this.player.y, 8, 0xe0b040);
    if (!key) this.physics.add.existing(spr);
    spr.setDisplaySize(20, 20);
    spr.body.allowGravity = !this._topdown;
    spr.setVelocity(dir * 300, this._topdown ? 0 : -140);
    spr.itemKind = it.kind;
    spr.itemName = it.name;
    spr.itemKey = it.key;
    this.thrown.add(spr);
    this.physics.add.collider(spr, this.platforms);
    this.time.delayedCall(1000, () => { if (spr.active) this._landItem(spr); });
    sfx('throw');
    playLog('throw');
    gfLog('tiraste ' + it.name);
    if (window.GC_ON_THROW) window.GC_ON_THROW(it);
  }

  _doAttack() {
    if (this._atkCd > 0 || this._dead) return;
    this._atkCd = 18;
    const hasSword = _inv.some((x) => x.kind === 'weapon');
    const dmg = (window.gcAttackDmg ? window.gcAttackDmg(hasSword, 6, GF_TEST.weaponDamage || GF_CONFIG.playerDmg) : (hasSword ? (GF_TEST.weaponDamage || GF_CONFIG.playerDmg || 12) : 6));
    const dir = this.player.flipX ? -1 : 1;
    const slash = this.add.rectangle(this.player.x + dir * (hasSword ? 32 : 24), this.player.y, hasSword ? 48 : 30, hasSword ? 28 : 22, 0x5cf2ff, 0.4);
    this.physics.add.existing(slash);
    slash.body.allowGravity = false;
    this.enemies.getChildren().forEach((e) => {
      if (e.active && Phaser.Geom.Intersects.RectangleToRectangle(slash.getBounds(), e.getBounds())) this._hurtEnemy(e, dmg);
    });
    this.time.delayedCall(hasSword ? 140 : 90, () => slash.destroy());
    this.player.setTint(0xf8fafc);
    this.time.delayedCall(70, () => this.player.clearTint());
    gfLog(hasSword ? 'ataque (espada)' : 'ataque (puño)');
    if (window.GC_ON_ATTACK) window.GC_ON_ATTACK({ dmg: dmg, hasSword: hasSword });
  }

  _doRoll() {
    if (this._dashCd > 0 || this._dead) return;
    this._dashCd = 48;
    this._iframes = 22;
    const dir = this.player.flipX ? -1 : 1;
    if (this._topdown) {
      const vx = (this.keys.left.isDown || this.keys.altLeft.isDown) ? -1 : (this.keys.right.isDown || this.keys.altRight.isDown) ? 1 : dir;
      const vy = (this.keys.up.isDown || this.keys.altUp.isDown) ? -1 : (this.keys.down.isDown || this.keys.altDown.isDown) ? 1 : 0;
      const len = Math.max(1, Math.hypot(vx, vy));
      this.player.setVelocity((vx / len) * 420, (vy / len) * 420);
    } else {
      this.player.setVelocity(dir * 500, -90);
    }
    this.player.setTint(0x94a3b8);
    this.time.delayedCall(160, () => this.player.clearTint());
    const dustDir = this.player.flipX ? -1 : 1;
    for (let i = 0; i < 4; i++) {
      const d = this.add.circle(this.player.x - dustDir * 8, this.player.y + 10, 3, 0xc8d4e4, 0.75);
      this.tweens.add({
        targets: d,
        x: d.x - dustDir * Phaser.Math.Between(8, 26),
        y: d.y + Phaser.Math.Between(-8, 10),
        alpha: 0,
        duration: 280,
        onComplete: () => d.destroy(),
      });
    }
    for (let i = 0; i < 3; i++) {
      this.time.delayedCall(i * 40, () => {
        if (!this.player || !this.player.active) return;
        const g = this.add.image(this.player.x, this.player.y, this.player.texture.key).setAlpha(0.32).setFlipX(this.player.flipX);
        this.tweens.add({ targets: g, alpha: 0, duration: 180, onComplete: () => g.destroy() });
      });
    }
    sfx('roll');
    playLog('roll');
    gfLog('roll / dash');
    if (window.GC_ON_ROLL) window.GC_ON_ROLL();
  }

  _doUse() {
    if (!_inv.length) return;
    const i = Math.min(_sel, _inv.length - 1);
    const it = _inv[i];
    if (it.kind === 'potion' || it.kind === 'item') {
      setHp(_hp + 28);
      _inv.splice(i, 1);
      if (_sel >= _inv.length) _sel = Math.max(0, _inv.length - 1);
      paintHud();
      sfx('use');
      playLog('use');
      gfLog('usaste poción');
      if (window.GC_ON_USE) window.GC_ON_USE(it);
    } else {
      gfLog('equipado: ' + it.name + ' (J para atacar)');
    }
  }

  _triggerGameOver() {
    if (this._dead) return;
    this._dead = true;
    this.physics.pause();
    this.player.setTint(0xef4444);
    document.getElementById('gf-gameover').style.display = 'flex';
    playLog('gameover');
    gfLog('GAME OVER — R o Espacio para reiniciar la escena');
  }

  update() {
    if (this._dead) return;
    if (this._iframes > 0) this._iframes--;

    document.getElementById('hud-fps').textContent =
      Math.round(this.game.loop.actualFps);

    const spd = 180 * this._speedMul;
    const onGround = this.player.body.blocked.down;
    const k = this.keys;
    if (this._atkCd > 0) this._atkCd--;
    if (this._dashCd > 0) this._dashCd--;
    if (this._poison) {
      this._poisonTick++;
      if (this._poisonTick % 90 === 0) setHp(_hp - 3);
    }

    if (this._topdown) {
      let vx = 0, vy = 0;
      if (k.left.isDown || k.altLeft.isDown) vx = -spd;
      else if (k.right.isDown || k.altRight.isDown) vx = spd;
      if (k.up.isDown || k.altUp.isDown) vy = -spd;
      else if (k.down.isDown || k.altDown.isDown) vy = spd;
      this.player.setVelocity(vx, vy);
    } else {
      if (k.left.isDown || k.altLeft.isDown) this.player.setVelocityX(-spd);
      else if (k.right.isDown || k.altRight.isDown) this.player.setVelocityX(spd);
      else this.player.setVelocityX(0);
      const jumpHeld = k.jump.isDown || k.up.isDown || k.altUp.isDown;
      const jumpPressed = jumpHeld && !this._wasJump;
      this._wasJump = jumpHeld;
      if (onGround) this._coyote = 8;
      else if (this._coyote > 0) this._coyote--;
      if (jumpPressed) this._jumpBuf = 8;
      else if (this._jumpBuf > 0) this._jumpBuf--;
      if (this._jumpBuf > 0 && this._coyote > 0) {
        this.player.setVelocityY(-420);
        this._jumpBuf = 0;
        this._coyote = 0;
        sfx('jump');
        playLog('jump');
      }
    }

    const moving = Math.abs(this.player.body.velocity.x) > 18 || (this._topdown && Math.abs(this.player.body.velocity.y) > 18);
    if (this.anims.exists('hero-walk')) {
      this.player.play(moving ? 'hero-walk' : 'hero-idle', true);
    }
    if (this.player.body.velocity.x < -8) this.player.setFlipX(true);
    else if (this.player.body.velocity.x > 8) this.player.setFlipX(false);

    if (GF_TEST.mechanics !== false) {
      const near = this._nearestLoot(52);
      if (this.prompt) {
        if (near) this.prompt.setVisible(true).setPosition(near.x, near.y - 22).setText('[E] ' + (near.itemName || 'objeto'));
        else this.prompt.setVisible(false);
      }
      if (k.interact && k.interact.isDown && near) {
        this._holdE++;
        if (this._holdE === 10) this._doPickup();
      } else this._holdE = 0;
      if (k.throw && Phaser.Input.Keyboard.JustDown(k.throw)) this._doThrow();
      if (k.attack && Phaser.Input.Keyboard.JustDown(k.attack)) this._doAttack();
      if (k.use && Phaser.Input.Keyboard.JustDown(k.use)) this._doUse();
      if (k.dash && Phaser.Input.Keyboard.JustDown(k.dash) && this._dashCd <= 0) this._doRoll();
      if (Phaser.Input.Keyboard.JustDown(k.one)) { _sel = 0; paintHud(); }
      if (Phaser.Input.Keyboard.JustDown(k.two)) { _sel = 1; paintHud(); }
      if (Phaser.Input.Keyboard.JustDown(k.three)) { _sel = 2; paintHud(); }
      if (Phaser.Input.Keyboard.JustDown(k.four)) { _sel = 3; paintHud(); }
      if (Phaser.Input.Keyboard.JustDown(k.five)) { _sel = 4; paintHud(); }
      if (Phaser.Input.Keyboard.JustDown(k.inv)) {
        _sel = (_sel + 1) % Math.max(1, (window.GC_INV_MAX || 5));
        paintHud();
      }
    }

    // enemy patrol bounce
    this.enemies.getChildren().forEach(e => {
      if (e.body.blocked.left)  e.setVelocityX(80);
      if (e.body.blocked.right) e.setVelocityX(-80);
    });

    // blink during invincibility
    this.player.setAlpha(this._iframes > 0 && Math.floor(this._iframes / 6) % 2 === 0 ? 0.4 : 1);

    const wpn = _inv.find((x) => x.kind === 'weapon');
    if (this.held) {
      this.held.setVisible(!!wpn);
      if (wpn) {
        const key = wpn.key && this.textures.exists(wpn.key) ? wpn.key : this.held.texture.key;
        if (this.textures.exists(key)) this.held.setTexture(key);
        this.held.setPosition(this.player.x + (this.player.flipX ? -12 : 12), this.player.y + 4);
        this.held.setFlipX(this.player.flipX);
      }
    }
  }
}

if (typeof Phaser === 'undefined') {
  document.body.insertAdjacentHTML('beforeend',
    '<div style="color:#5cf2ff;font:13px monospace;padding:24px">No se cargó Phaser (CDN). Revisa red/adblock y pulsa Run de nuevo.</div>');
} else {
new Phaser.Game({
  type: Phaser.AUTO,
  width: 720,
  height: 480,
  backgroundColor: '#151a24',
  parent: document.body,
  physics: {
    default: 'arcade',
    arcade: { gravity: { y: ${gravityY} }, debug: !!GF_TEST.hitboxes }
  },
  scene: [BootScene, MenuScene, MainScene],
});
}
</script>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------
export default function SimulatorPanel({ project, onChange, testConfig }) {
  const { t } = useI18n();
  const iframeRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [logs, setLogs] = useState([]);
  const playEvents = useRef([]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === "gf-log") setLogs((l) => [...l.slice(-24), e.data.msg]);
      if (e.data?.type === "gf-playlog") playEvents.current.push(e.data.evt);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Rebuild & inject HTML whenever project data changes while running
  const inject = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    const html = buildPhaserHTML(project, testConfig);
    const blob = new Blob([html], { type: "text/html" });
    frame.src = URL.createObjectURL(blob);
  }, [project, testConfig]);

  const handleRun = () => {
    playEvents.current = [];
    setRunning(true);
    setTimeout(inject, 50);
  };

  const flushPlayLog = () => {
    const evts = playEvents.current.slice();
    playEvents.current = [];
    if (!evts.length || !onChange) return;
    const note = `Auto: ${evts.slice(0, 24).join(" · ")}`;
    const prev = project.config?.playtest_log || [];
    const cards = playEventsToKanban(evts);
    const patch = {
      config: {
        ...(project.config || {}),
        playtest_log: [...prev, { ts: new Date().toISOString(), note, events: evts }],
      },
    };
    if (cards.length) {
      const columns = (project.kanban?.columns || []).map((c) => (
        c.id === "todo" ? { ...c, cards: [...(c.cards || []), ...cards] } : c
      ));
      if (columns.length) patch.kanban = { ...(project.kanban || {}), columns };
    }
    onChange(patch);
  };

  const handlePause = () => {
    flushPlayLog();
    setRunning(false);
    if (iframeRef.current) iframeRef.current.src = "about:blank";
  };

  const handleReset = () => {
    if (running) inject();
  };

  const handleCapture = () => {
    // We can't canvas-capture a cross-origin iframe, so ask the user
    toast.info("Para capturar el thumbnail, usa el botón dentro del juego o haz screenshot manual.");
  };

  const handleExportHTML = () => {
    const html = buildPhaserHTML(project, testConfig);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project.slug || "game"}-preview.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("HTML exportado — ábrelo en cualquier navegador.");
  };

  const controls = project.controls?.keyboard || {};
  const spriteCount = (project.sprites || []).length;
  const fileCount = (project.files || []).length;
  const entityCount = flattenEntities(project).length;

  return (
    <div className={`p-4 flex flex-col gap-4 ${fullscreen ? "fixed inset-0 z-50 bg-zinc-950 p-4" : ""}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {!running ? (
          <Button
            data-testid="sim-run-btn"
            onClick={handleRun}
            className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold gap-2"
          >
            <Play size={14} weight="fill" /> {t("sim.run")}
          </Button>
        ) : (
          <Button
            data-testid="sim-pause-btn"
            onClick={handlePause}
            className="bg-yellow-400 hover:bg-yellow-300 text-zinc-950 rounded-none font-semibold gap-2"
          >
            <Pause size={14} weight="fill" /> {t("sim.pause")}
          </Button>
        )}
        <Button
          data-testid="sim-reset-btn"
          onClick={handleReset}
          disabled={!running}
          variant="ghost"
          className="rounded-none text-zinc-300 border border-zinc-800 hover:bg-zinc-900 gap-2"
        >
          <ArrowCounterClockwise size={14} /> {t("sim.reset")}
        </Button>
        <Button
          onClick={handleExportHTML}
          variant="ghost"
          className="rounded-none text-cyan-300 border border-zinc-800 hover:bg-zinc-900 gap-2"
        >
          <Camera size={14} /> Export HTML
        </Button>
        <Button
          onClick={() => setFullscreen(f => !f)}
          variant="ghost"
          className="rounded-none text-zinc-400 border border-zinc-800 hover:bg-zinc-900 gap-2"
        >
          <ArrowsOut size={14} /> {fullscreen ? "Exit" : "Fullscreen"}
        </Button>
        <button
          onClick={() => setShowInfo(i => !i)}
          className="text-zinc-600 hover:text-zinc-400 ml-auto"
        >
          <Info size={16} />
        </button>
      </div>

      {/* Info bar */}
      {showInfo && (
        <div className="border border-zinc-800 bg-zinc-900 px-4 py-3 text-xs font-mono text-zinc-400 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><span className="text-zinc-600">Sprites cargados:</span><br/><span className="text-cyan-400">{spriteCount}</span></div>
          <div><span className="text-zinc-600">Archivos de código:</span><br/><span className="text-cyan-400">{fileCount}</span></div>
          <div><span className="text-zinc-600">Entidades GDD:</span><br/><span className="text-cyan-400">{entityCount}</span></div>
          <div><span className="text-zinc-600">Motor:</span><br/><span className="text-cyan-400">Phaser 3 + Arcade Physics</span></div>
        </div>
      )}

      {/* Main area: iframe + sidebar */}
      <div className="flex gap-4 flex-col lg:flex-row">
        <div className="flex-1">
          <div className="border border-zinc-800 bg-zinc-900">
            {!running ? (
              <div
                className="w-full flex flex-col items-center justify-center text-zinc-600 cursor-pointer hover:bg-zinc-900/80 transition-colors"
                style={{ height: 480 }}
                onClick={handleRun}
              >
                <Play size={48} weight="thin" className="mb-4 text-zinc-700" />
                <div className="text-[11px] tracking-[0.4em] uppercase">Presiona RUN para iniciar</div>
                <div className="text-[10px] tracking-[0.25em] text-zinc-700 mt-2">
                  Phaser 3 · Arcade Physics · {spriteCount} sprites · {fileCount} archivos
                </div>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                title="GameForge Simulator"
                data-testid="sim-canvas"
                sandbox="allow-scripts"
                style={{ width: 720, height: 480, maxWidth: "100%", border: "none", display: "block" }}
              />
            )}
          </div>
          <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-zinc-600">
            // Phaser 3.80 · Arcade Physics · Motor real en iframe sandbox
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:w-48 space-y-3">
          <InfoCard label="Motor" value="Phaser 3" tone="cyan" />
          <InfoCard label="Física" value="Arcade" tone="cyan" />
          <InfoCard label="Cámara" value={project.config?.camera === "topdown" ? "Arriba" : "Lado"} tone="cyan" />
          <InfoCard label="Sprites" value={spriteCount} tone={spriteCount > 0 ? "cyan" : "dim"} />
          <InfoCard label="Código" value={`${fileCount} files`} tone={fileCount > 0 ? "cyan" : "dim"} />
          <InfoCard label="Entidades" value={entityCount} tone={entityCount > 0 ? "cyan" : "dim"} />
          <div className="border border-zinc-800 p-3 max-h-36 overflow-auto gf-scroll">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Log</div>
            <div className="font-mono text-[10px] text-zinc-500 space-y-0.5">
              {logs.length === 0 && <div>—</div>}
              {logs.map((m, i) => <div key={i}>{m}</div>)}
            </div>
          </div>
          <div className="border border-zinc-800 p-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Controles</div>
            <div className="font-mono text-xs text-zinc-400 space-y-1">
              <div>↑ {controls.move_up || "W"}</div>
              <div>↓ {controls.move_down || "S"}</div>
              <div>← {controls.move_left || "A"}</div>
              <div>→ {controls.move_right || "D"}</div>
              <div>⬆ {controls.jump || "Space"} salto</div>
              <div>E {controls.interact || "E"} agarrar</div>
              <div>F {controls.throw || "F"} tirar</div>
              <div>J {controls.attack || "J"} atacar</div>
              <div>⇧ {controls.ability || "Shift"} roll</div>
              <div>Q {controls.secondary || "Q"} usar</div>
              <div>1-5 inventario</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function InfoCard({ label, value, tone = "default" }) {
  const colors = {
    cyan: "text-cyan-400",
    dim: "text-zinc-600",
    default: "text-zinc-100",
  };
  return (
    <div className="border border-zinc-800 p-3" >
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{label}</div>
      <div className={`font-mono text-lg mt-1 ${colors[tone]}`}>{value}</div>
    </div>
  );
}